import { Box, Grid, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Section, SectionHeader } from "../ui/primitives";
import { colors } from "../ui/tokens";
import PageHero from "../widgets/PageHero";
import CtaBand from "../widgets/CtaBand";

const SERVICES = ["s1", "s2", "s3", "s4"];
const STEPS = ["s1", "s2", "s3", "s4", "s5"];
const TECH = {
    mobile: ["Flutter", "Swift / SwiftUI", "Kotlin"],
    web: ["React", "Next.js", "TypeScript"],
    backend: ["Python / Django", "Node.js", "PostgreSQL", "MySQL", "MongoDB", "Firebase"],
    cloud: ["AWS", "Terraform", "Docker", "Kubernetes", "GitHub Actions"],
    design: ["Figma", "Adobe XD", "Sketch"],
};

export function Services() {
    const { t } = useTranslation("ServicesPage");

    return (
        <>
            <Helmet>
                <title>{t("meta.title")}</title>
                <meta name="description" content={t("meta.description")} data-rh="true" />
                <link rel="canonical" href="https://oxide-digital.com/services" />
            </Helmet>
            <PageHero eyebrow={t("hero.eyebrow")} title={t("hero.title")} lead={t("hero.lead")} />

            <Section tone="paper" py={{ xs: 6, md: 10 }}>
                {SERVICES.map((k, i) => (
                    <Box key={k} sx={{ py: { xs: 5, md: 7 }, borderTop: i === 0 ? "none" : `1px solid ${colors.line}` }}>
                        <Grid container spacing={{ xs: 2, md: 6 }}>
                            <Grid item xs={12} md={1}>
                                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: colors.orangeDark, pt: { md: 1 } }}>
                                    {String(i + 1).padStart(2, "0")}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h2" component="h2" sx={{ fontSize: { xs: "1.7rem", md: "2.1rem" }, mb: 2 }}>
                                    {t(`list.${k}.title`)}
                                </Typography>
                                <Typography sx={{ color: colors.body, fontSize: "1.05rem" }}>{t(`list.${k}.body`)}</Typography>
                                <Typography sx={{ mt: 2.5, fontSize: "0.9rem", color: colors.muted }}>
                                    <Box component="span" sx={{ fontWeight: 700, color: colors.ink }}>
                                        {t("list.examplesLabel")}:{" "}
                                    </Box>
                                    {t(`list.${k}.example`)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, pt: { md: 1 } }}>
                                    {t(`list.${k}.points`, { returnObjects: true }).map((point) => (
                                        <Box
                                            component="li"
                                            key={point}
                                            sx={{ py: 1.4, borderBottom: `1px solid ${colors.line}`, color: colors.ink, fontWeight: 500 }}
                                        >
                                            {point}
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Section>

            <Section tone="deep" py={{ xs: 9, md: 13 }} id="process">
                <SectionHeader eyebrow={t("process.eyebrow")} title={t("process.title")} lead={t("process.lead")} dark />
                <Grid container spacing={{ xs: 0, md: 3 }}>
                    {STEPS.map((k, i) => (
                        <Grid item xs={12} md key={k}>
                            <Box
                                sx={{ borderTop: `2px solid ${i === 0 ? colors.orange : colors.lineDark}`, pt: 2.5, pb: { xs: 4, md: 0 } }}
                            >
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: colors.orange, mb: 1.5 }}>
                                    {String(i + 1).padStart(2, "0")}
                                </Typography>
                                <Typography variant="h4" component="h3" sx={{ color: colors.onDark, mb: 1.25 }}>
                                    {t(`process.steps.${k}.title`)}
                                </Typography>
                                <Typography sx={{ color: colors.onDarkMuted, fontSize: "0.95rem" }}>
                                    {t(`process.steps.${k}.body`)}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            <Section tone="paper" py={{ xs: 9, md: 12 }}>
                <SectionHeader eyebrow={t("tech.eyebrow")} title={t("tech.title")} />
                <Box>
                    {Object.entries(TECH).map(([group, items]) => (
                        <Box key={group} sx={{ py: 2.5, borderTop: `1px solid ${colors.line}` }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <Typography sx={{ fontWeight: 700, color: colors.ink }}>{t(`tech.groups.${group}`)}</Typography>
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Typography sx={{ color: colors.body }}>{items.join("  ·  ")}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </Box>
            </Section>

            <CtaBand />
        </>
    );
}
